import React, {forwardRef, useEffect, useImperativeHandle, useState} from 'react';
import {CloseOutline} from 'antd-mobile-icons';
import {ImageViewer, Picker, SpinLoading, Toast} from 'antd-mobile';
import {gql, useLazyQuery, useMutation, useQuery} from '@apollo/client';
import i18n from '../../../i18n';
import {useTranslation} from 'react-i18next';
import {showError} from '../../../utils/exceptionUtils';
import {GET_NEXT_ACTION_LIST, NextActionListResponse} from '../../../graphql/queries/getNextActionList';
import {
    ExceptionDictionaryByCodeResponse,
    GET_EXCEPTION_DICTIONARY_BY_REASON_CODE
} from '../../../graphql/queries/exceptionQueries';

// GraphQL Mutation for setting next action
const HANDLE_SET_NEXT_ACTION_SCAN_EVENT = gql`
    mutation HandleSetNextActionScanEvent($barCode: String!, $actionName: String!) {
        handleSetNextActionScanEvent(barCode: $barCode, actionName: $actionName)
    }
`;

interface OrderDetailCardOldProps {
    trackingNumber: string;
    overallStatus: string;
    exceptionReasonCode?: string;
    deliveryAttempts?: number;
    sortingCode?: string;
    nextAction?: string;
    isAbnormal?: boolean;
    exceptionImageS3Keys?: string[];
    exceptionImageS3UrlsJson?: string[];
    onClose: () => void;
    onNextActionChanged?: (newNextAction: string) => void; // 回调函数，用于通知父组件刷新数据
}

export interface OrderDetailCardOldRef {
    close: () => void;
}

export const OrderDetailCardOld = forwardRef<OrderDetailCardOldRef, OrderDetailCardOldProps>(({
    trackingNumber,
    overallStatus,
    exceptionReasonCode,
    deliveryAttempts,
    sortingCode,
    nextAction,
    isAbnormal,
    exceptionImageS3Keys,
    exceptionImageS3UrlsJson,
    onClose,
    onNextActionChanged
}, ref) => {
    const { t } = useTranslation();
    const [imageUrls, setImageUrls] = useState<string[]>([]);
    const [isLoadingImages, setIsLoadingImages] = useState(false);
    const [imagesLoaded, setImagesLoaded] = useState(false);
    const [imageViewerVisible, setImageViewerVisible] = useState(false);
    const [currentImageUrl, setCurrentImageUrl] = useState<string>('');
    const [selectedAction, setSelectedAction] = useState<string | null>(nextAction || null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [pickerVisible, setPickerVisible] = useState(false);
    // 异常字典查询
    const [getExceptionDictionary, { data: exceptionDictData }] = useLazyQuery<ExceptionDictionaryByCodeResponse>(GET_EXCEPTION_DICTIONARY_BY_REASON_CODE);

    // 当nextAction prop变化时，更新selectedAction
    useEffect(() => {
        setSelectedAction(nextAction || null);
    }, [nextAction]);

    // 当trackingNumber变化时，重置图片相关状态
    useEffect(() => {
        setImageUrls([]);
        setImagesLoaded(false);
        setIsLoadingImages(false);
        setImageViewerVisible(false);
        setCurrentImageUrl('');
    }, [trackingNumber]);

    // 暴露关闭方法给父组件
    useImperativeHandle(ref, () => ({
        close: onClose
    }));

    // 当异常原因代码存在时，查询异常字典
    useEffect(() => {
        if (exceptionReasonCode && isAbnormal) {
            getExceptionDictionary({ variables: { exceptionReasonCode } });
        }
    }, [exceptionReasonCode, isAbnormal, getExceptionDictionary]);

    // 获取国际化的状态显示文本
    const getLocalizedStatus = (status: string) => {
        return t(`enum.overallStatus.${status}`, status);
    };

    // 获取国际化的异常原因显示文本
    const getLocalizedExceptionReason = (reasonCode?: string) => {
        if (!reasonCode || !exceptionDictData?.getExceptionDictionaryByReasonCode) {
            return reasonCode || '-';
        }

        const exceptionDict = exceptionDictData.getExceptionDictionaryByReasonCode;
        const currentLang = i18n.language || 'en';

        // 根据语言选择对应字段
        if (currentLang.startsWith('zh')) {
            return exceptionDict.secondaryTypeZh || exceptionDict.secondaryTypeEn || reasonCode;
        } else if (currentLang.startsWith('es')) {
            return exceptionDict.secondaryTypeEs || exceptionDict.secondaryTypeEn || reasonCode;
        } else {
            // 默认英文
            return exceptionDict.secondaryTypeEn || exceptionDict.secondaryTypeZh || reasonCode;
        }
    };

    // 根据NextAction获取对应的颜色样式
    const getActionColorStyle = (action?: string | null) => {
        if (!action) return '';
        
        switch (action.toUpperCase()) {
            case 'HOLD':
                return 'border-blue-600'; // 深一点的亮蓝色 (#2563eb)
            case 'REDELIVER':
                return 'border-green-500'; // 明亮的绿色 (#22c55e)
            case 'RETURN':
                return 'border-red-500'; // 鲜艳红色 (#ef4444)
            case 'RESORT':
            case 'COMPENSATE':
                return 'border-orange-500'; // 明亮橙色 (#f97316)
            default:
                return 'border-gray-300'; // 默认灰色边框
        }
    };
    
    const [handleSetNextActionScanEvent] = useMutation(HANDLE_SET_NEXT_ACTION_SCAN_EVENT);

    // 获取下一步动作选项
    const { data: nextActionData, loading: nextActionLoading } = useQuery<NextActionListResponse>(GET_NEXT_ACTION_LIST, {
        fetchPolicy: 'cache-first'
    });

    // 转换为Picker组件需要的格式，并应用国际化
    const nextActionOptions = React.useMemo(() => {
        if (!nextActionData?.getNextActionList) {
            return [[]];
        }
        
        return [
            nextActionData.getNextActionList
                .filter((action) => action.name !== 'None' && action.name !== 'Compensate') // 过滤掉None和Compensate选项
                .map((action) => ({
                    label: t(`pda.common.exceptionActions.${action.name}`),
                    value: action.name
                }))
        ];
    }, [nextActionData]);

    // 处理下拉框选择并直接提交
    const handleActionSelect = async (value: any[]) => {
        const actionName = value[0] as string;
        if (!actionName) return;

        // 如果选择的动作和当前动作相同，不需要请求
        if (actionName === selectedAction) {
            setPickerVisible(false);
            return;
        }

        const actionLabel = nextActionOptions[0].find((opt: any) => opt.value === actionName)?.label || actionName;

        setIsSubmitting(true);
        setPickerVisible(false);
        try {
            const { data } = await handleSetNextActionScanEvent({
                variables: {
                    barCode: trackingNumber,
                    actionName: actionName
                }
            });

            if (data?.handleSetNextActionScanEvent) {
                // 更新本地状态
                setSelectedAction(actionName);

                Toast.show({
                    content: t('pda.pages.returnToWhs.setNextActionSuccess', {
                        trackingNumber,
                        action: actionLabel
                    }),
                    position: 'center',
                    duration: 2000
                });

                        // 通知父组件刷新数据
                        if (onNextActionChanged) {
                            onNextActionChanged(actionName);
                        }
            } else {
                throw new Error('Failed to set next action');
            }
        } catch (error) {
            showError({
                title: t('pda.common.error'),
                message: t('pda.pages.returnToWhs.setNextActionFailed')
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Prevent background scrolling when overlay is shown
    useEffect(() => {
        // Save current scroll position and prevent scrolling
        const originalStyle = window.getComputedStyle(document.body).overflow;
        document.body.style.overflow = 'hidden';
        
        // Cleanup function to restore scrolling when component unmounts
        return () => {
            document.body.style.overflow = originalStyle;
        };
    }, []);

    const handleViewPhotos = async () => {
        // 优先使用 exceptionImageS3UrlsJson，如果没有则使用 exceptionImageS3Keys
        const imageData = exceptionImageS3UrlsJson && exceptionImageS3UrlsJson.length > 0 
            ? exceptionImageS3UrlsJson 
            : exceptionImageS3Keys;
            
        if (!imageData || imageData.length === 0) {
            return;
        }

        setIsLoadingImages(true);
        try {
            // 如果是 exceptionImageS3UrlsJson，直接使用URL
            // 如果是 exceptionImageS3Keys，需要转换为URL（这里暂时直接使用）
            setImageUrls(imageData);
            setImagesLoaded(true);
        } catch (error) {
            showError({ title: t('pda.common.error'), message: t('pda.pages.returnToWhs.failedToLoadPhotos') });
        } finally {
            setIsLoadingImages(false);
        }
    };

    return (
        <>
            {/* Background overlay to prevent interaction with bottom layer */}
            <div 
                className="fixed inset-0 z-40 bg-black bg-opacity-50"
                onTouchMove={(e) => e.preventDefault()}
                onWheel={(e) => e.preventDefault()}
            />
            
            {/* Full screen overlay */}
            <div className={`fixed inset-0 z-50 bg-white shadow-lg max-h-screen border-8 ${getActionColorStyle(selectedAction)}`}>
                {/* Header with tracking number and close button */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
                    <span className="text-lg font-semibold text-black">{trackingNumber}</span>
                    <button onClick={onClose} className="p-1">
                        <CloseOutline fontSize={24} color="#999" />
                    </button>
                </div>
                
                {/* Scrollable content area */}
                <div className="flex-1 overflow-y-auto p-4" style={{ maxHeight: 'calc(100vh - 80px)' }}>

                {/* Order Information - Table with border */}
                <div className="mb-4 border border-gray-300 rounded-lg overflow-hidden bg-white">
                    {/* Status */}
                    <div className="flex border-b border-gray-200">
                        <div className="w-2/5 px-4 py-3 border-r border-gray-200 bg-gray-50">
                            <span className="text-gray-400 text-base">{t('pda.pages.returnToWhs.status')}</span>
                        </div>
                        <div className="flex-1 px-4 py-3 bg-white">
                            <div className="text-base font-medium text-black text-center break-words" style={{ wordBreak: 'break-word' }}>{getLocalizedStatus(overallStatus)}</div>
                        </div>
                    </div>

                    {/* Reason - 只在isAbnormal为true时显示 */}
                    {isAbnormal && (
                        <div className="flex border-b border-gray-200">
                            <div className="w-2/5 px-4 py-3 border-r border-gray-200 bg-gray-50">
                                <span className="text-gray-400 text-base">{t('pda.pages.returnToWhs.reason')}</span>
                            </div>
                            <div className="flex-1 px-4 py-3 bg-white">
                                <div className="text-base font-medium text-black text-center break-words" style={{ wordBreak: 'break-word' }}>{getLocalizedExceptionReason(exceptionReasonCode)}</div>
                            </div>
                        </div>
                    )}

                    {/* Attempts */}
                    {deliveryAttempts !== undefined && (
                        <div className="flex border-b border-gray-200">
                            <div className="w-2/5 px-4 py-3 border-r border-gray-200 bg-gray-50">
                                <span className="text-gray-400 text-base">{t('pda.pages.returnToWhs.attempts')}</span>
                            </div>
                            <div className="flex-1 px-4 py-3 flex items-center justify-center bg-white">
                                <span className="text-base font-medium text-black">{deliveryAttempts}</span>
                            </div>
                        </div>
                    )}

                    {/* Route */}
                    {sortingCode && (
                        <div className="flex">
                            <div className="w-2/5 px-4 py-3 border-r border-gray-200 bg-gray-50">
                                <span className="text-gray-400 text-base">{t('pda.pages.returnToWhs.route')}</span>
                            </div>
                            <div className="flex-1 px-4 py-3 flex items-center justify-center bg-white">
                                <span className="text-base font-medium text-black">{sortingCode}</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Next Action - Combined Display and Selection - 只在isAbnormal为true时显示 */}
                {isAbnormal && (
                    <div className="py-3">
                        <div className="mb-2">
                            <span className="text-gray-400 text-base">{t('pda.common.nextAction')}</span>
                        </div>
                        
                        {/* Next Action Dropdown - Shows current and allows selection */}
                        <div className="mb-3">
                            <button
                                className="w-full py-3 px-4 text-base border border-gray-300 rounded-lg bg-white flex items-center justify-center disabled:opacity-50 relative"
                                onClick={() => setPickerVisible(true)}
                                disabled={isSubmitting || nextActionLoading}
                            >
                                <span 
                                    className={selectedAction ? 'text-black' : 'text-gray-400'}
                                    style={{ fontWeight: '900', fontFamily: 'system-ui, -apple-system, sans-serif' }}
                                >
                                    {nextActionLoading 
                                        ? t('pda.common.loading')
                                        : selectedAction 
                                            ? nextActionOptions[0].find((opt: any) => opt.value === selectedAction)?.label 
                                            : t('pda.pages.returnToWhs.selectNextAction')
                                    }
                                </span>
                                <span className="text-gray-400 absolute right-4">▼</span>
                            </button>
                        </div>
                    </div>
                )}

                {/* View Photos Button - 只在isAbnormal为true且有图片时显示 */}
                {isAbnormal && ((exceptionImageS3UrlsJson && exceptionImageS3UrlsJson.length > 0) || (exceptionImageS3Keys && exceptionImageS3Keys.length > 0)) && (
                    <div className="pb-4">
                        <button
                            className="w-full py-3 text-center text-base font-medium text-black bg-gray-100 rounded-lg disabled:opacity-50"
                            onClick={handleViewPhotos}
                            disabled={isLoadingImages}
                        >
                            {isLoadingImages ? (
                                <div className="flex items-center justify-center gap-2">
                                    <SpinLoading />
                                    <span>{t('pda.pages.returnToWhs.loadingPhotos')}</span>
                                </div>
                            ) : (
                                t('pda.pages.returnToWhs.viewPhotos')
                            )}
                        </button>
                    </div>
                )}

                {/* Thumbnail previews */}
                {imagesLoaded && imageUrls.length > 0 && (
                    <div className="mt-3 grid grid-cols-2 gap-3">
                        {imageUrls.map((url, index) => (
                            <div
                                key={index}
                                className="aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-pointer shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
                                onClick={() => {
                                    setCurrentImageUrl(url);
                                    setImageViewerVisible(true);
                                }}
                            >
                                <img
                                    src={url}
                                    alt={`Exception ${index + 1}`}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        target.style.display = 'none';
                                        const parent = target.parentElement;
                                        if (parent) {
                                            parent.innerHTML = `<div class="w-full h-full flex items-center justify-center text-gray-400 text-sm">${t('pda.pages.returnToWhs.imageLoadFailed')}</div>`;
                                        }
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                )}
                </div>
            </div>

            {/* Image Viewer */}
            <ImageViewer
                image={currentImageUrl}
                visible={imageViewerVisible}
                onClose={() => setImageViewerVisible(false)}
            />

            {/* Next Action Picker */}
            <Picker
                columns={nextActionOptions}
                visible={pickerVisible}
                onClose={() => setPickerVisible(false)}
                onConfirm={handleActionSelect}
                value={selectedAction ? [selectedAction] : []}
                title={t('pda.pages.returnToWhs.selectNextAction')}
            />

        </>
    );
});

